import React, { useCallback, useEffect, useMemo } from 'react'
import { displayDollar, displayStatus } from '../../../lib/utils/helpers'
import { InvestmentStatus_v1 } from '../../../lib/types/propsType_v1'
import {
  labelInterestIncomePreference,
  parseInterestIncomePreference,
} from '../../invest-v2/invest-v2-utils'
import { investmentNeedsWalletPayment, withUpperScaleSuffix } from '../dashboard-v2.utils'
import { DashboardV2ProgressStepper } from './DashboardV2ProgressStepper'
import type { OngoingInvestmentRow } from './OngoingInvestmentsSection'

type OngoingInvestmentDetailsModalProps = {
  isOpen: boolean
  investment: OngoingInvestmentRow | null
  onClose: () => void
  onViewPaymentSummary?: (investment: OngoingInvestmentRow) => void
  onCompletePayment?: (investment: OngoingInvestmentRow) => void
}

type InvestmentRequestMetadata = OngoingInvestmentRow['metadata'] & {
  investmentInitiatedDate?: string
  investmentOrderPlacedDate?: string
  paymentReceivedDate?: string
  investmentApprovalDate?: string
  tradeExecutionDate?: string
  transactionNumber?: string
}

type DetailItemProps = {
  label: string
  value: string
}

const STEP_LABELS: [string, string, string] = ['Request', 'Payment', 'Execution']
const REINVEST_STEP_LABELS: [string, string, string] = ['Request', 'Interest credit', 'Execution']

const PAYMENT_SUMMARY_STATUSES: string[] = [
  InvestmentStatus_v1.PAYMENT_DONE,
  InvestmentStatus_v1.ORDER_PLACED,
]

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div className='dashboard-v2__withdrawal-form__detail-item'>
    <span className='dashboard-v2__withdrawal-form__detail-label'>{label}</span>
    <span className='dashboard-v2__withdrawal-form__detail-value'>{value}</span>
  </div>
)

const formatUsdFromCents = (cents: number) =>
  withUpperScaleSuffix(displayDollar(String(Math.round(cents)), 'USD', 2, false))

const formatDate = (iso?: string) => {
  if (!iso) return '—'
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const getStepPhases = (status: string, needsWalletPayment = false) => {
  switch (status) {
    case InvestmentStatus_v1.INITIATED:
      return {
        request: 'current' as const,
        payment: 'upcoming' as const,
        executed: 'upcoming' as const,
      }
    case InvestmentStatus_v1.PAYMENT_DONE:
      return {
        request: 'done' as const,
        payment: 'done' as const,
        executed: 'upcoming' as const,
      }
    case InvestmentStatus_v1.ORDER_PLACED:
      // Wallet payment happens before the order is placed; only legacy unpaid rows still wait on payment.
      return needsWalletPayment
        ? { request: 'done' as const, payment: 'current' as const, executed: 'upcoming' as const }
        : { request: 'done' as const, payment: 'done' as const, executed: 'current' as const }
    case InvestmentStatus_v1.REINVESTMENT_PENDING:
      return {
        request: 'done' as const,
        payment: 'done' as const,
        executed: 'current' as const,
      }
    default:
      return {
        request: 'current' as const,
        payment: 'upcoming' as const,
        executed: 'upcoming' as const,
      }
  }
}

const investmentDetailsFooterNote = (status: string, needsWalletPayment = false): string => {
  switch (status) {
    case InvestmentStatus_v1.PAYMENT_DONE:
      return 'Your wallet payment is complete and your order is being placed.'
    case InvestmentStatus_v1.ORDER_PLACED:
      return needsWalletPayment
        ? 'Wallet payment is still required to complete this investment. Your signed termsheet remains in place.'
        : 'Your payment is complete and your order has been placed. The Credibila team will execute the trade and allocate your units.'
    case InvestmentStatus_v1.REINVESTMENT_PENDING:
      return 'Your interest reinvestment request is pending unit allocation. No bank payout will be made for this interest amount.'
    default:
      return 'This investment is in progress.'
  }
}

export const OngoingInvestmentDetailsModal: React.FC<OngoingInvestmentDetailsModalProps> = ({
  isOpen,
  investment,
  onClose,
  onViewPaymentSummary,
  onCompletePayment,
}) => {
  const resetAndClose = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetAndClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, resetAndClose])

  const requestMetadata = investment?.metadata as InvestmentRequestMetadata | undefined

  const amountDisplay = useMemo(() => {
    if (!investment) return '—'
    return withUpperScaleSuffix(displayDollar(String(investment.amount), 'USD', 2, false))
  }, [investment])

  const phases = useMemo(
    () =>
      investment
        ? getStepPhases(
            investment.status,
            investmentNeedsWalletPayment(investment.status, investment.metadata),
          )
        : null,
    [investment],
  )

  const timelineItems = useMemo(() => {
    if (!investment) return []
    const meta = requestMetadata
    return [
      { label: 'Investment request submitted', value: formatDate(meta?.investmentInitiatedDate) },
      { label: 'Investment order processed', value: formatDate(meta?.investmentOrderPlacedDate) },
      { label: 'Units allocated', value: formatDate(meta?.tradeExecutionDate) },
    ].filter((item) => item.value !== '—')
  }, [investment, requestMetadata])

  const interestPreferenceLabel = useMemo(() => {
    if (!investment) return null
    const meta = investment.metadata as Record<string, unknown>
    const hasPref =
      (meta?.interestIncomePreference !== null && meta?.interestIncomePreference !== undefined) ||
      (meta?.interest_income_preference !== null && meta?.interest_income_preference !== undefined)
    if (!hasPref) return null
    return labelInterestIncomePreference(parseInterestIncomePreference(investment.metadata))
  }, [investment])

  if (!isOpen || !investment) return null

  const needsWalletPayment = investmentNeedsWalletPayment(investment.status, investment.metadata)
  const canCompletePayment =
    typeof onCompletePayment === 'function' &&
    investment.opportunity_id !== null &&
    investment.opportunity_id !== undefined &&
    needsWalletPayment
  const canViewPaymentSummary =
    typeof onViewPaymentSummary === 'function' &&
    PAYMENT_SUMMARY_STATUSES.includes(investment.status) &&
    investment.opportunity_id !== null &&
    investment.opportunity_id !== undefined

  return (
    <div
      className='dashboard-v2__withdrawal-modal-backdrop'
      role='presentation'
      onClick={(e) => {
        if (e.target === e.currentTarget) resetAndClose()
      }}
    >
      <div
        className='dashboard-v2__withdrawal-form'
        role='dialog'
        aria-modal='true'
        aria-labelledby='ongoing-investment-details-title'
        onClick={(e) => e.stopPropagation()}
        data-testid='ongoing-investment-details-modal'
      >
        <header className='dashboard-v2__withdrawal-form__header'>
          <div className='dashboard-v2__withdrawal-form__header-text'>
            <p className='dashboard-v2__withdrawal-form__eyebrow'>Ongoing investment</p>
            <h2 id='ongoing-investment-details-title'>Investment details</h2>
            <div className='dashboard-v2__withdrawal-form__asset-chip'>
              <span className='dashboard-v2__withdrawal-form__asset-chip-title'>
                {investment.opportunity_title || 'Investment'}
              </span>
              {investment.asset_class ? (
                <span className='dashboard-v2__withdrawal-form__asset-chip-badge'>
                  {investment.asset_class}
                </span>
              ) : null}
            </div>
            <p className='dashboard-v2__withdrawal-form__subtitle'>
              Status: {displayStatus(investment.status)} · Amount: {amountDisplay}
            </p>
          </div>
          <div
            role='button'
            tabIndex={0}
            aria-label='Close dialog'
            className='dashboard-v2__holdings-actions-panel__close'
            onClick={resetAndClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                resetAndClose()
              }
            }}
          >
            ×
          </div>
        </header>

        <div className='dashboard-v2__withdrawal-form__form'>
          {phases ? (
            <section className='dashboard-v2__withdrawal-form__section'>
              <h3 className='dashboard-v2__withdrawal-form__section-title'>Progress</h3>
              <DashboardV2ProgressStepper
                request={phases.request}
                payment={phases.payment}
                executed={phases.executed}
                labels={
                  investment.status === InvestmentStatus_v1.REINVESTMENT_PENDING
                    ? REINVEST_STEP_LABELS
                    : STEP_LABELS
                }
              />
            </section>
          ) : null}

          <section className='dashboard-v2__withdrawal-form__section'>
            <h3 className='dashboard-v2__withdrawal-form__section-title'>Investment information</h3>
            <div className='dashboard-v2__withdrawal-form__details-grid'>
              <DetailItem label='Opportunity' value={investment.opportunity_title || '—'} />
              <DetailItem label='Commitment amount' value={amountDisplay} />
              <DetailItem label='Status' value={displayStatus(investment.status)} />
              {investment.order_id ? (
                <DetailItem label='Order ID' value={investment.order_id} />
              ) : null}
              <DetailItem label='Started' value={formatDate(investment.created_at)} />
              {interestPreferenceLabel ? (
                <DetailItem label='Interest preference' value={interestPreferenceLabel} />
              ) : null}
              {requestMetadata?.transactionNumber ? (
                <DetailItem
                  label='Transaction reference'
                  value={requestMetadata.transactionNumber}
                />
              ) : null}
              {investment.platform_fee?.amount ? (
                <DetailItem
                  label='Platform fee'
                  value={formatUsdFromCents(investment.platform_fee.amount)}
                />
              ) : null}
            </div>
          </section>

          {timelineItems.length > 0 ? (
            <section className='dashboard-v2__withdrawal-form__section'>
              <h3 className='dashboard-v2__withdrawal-form__section-title'>Timeline</h3>
              <div className='dashboard-v2__withdrawal-form__details-grid'>
                {timelineItems.map((item) => (
                  <DetailItem key={item.label} label={item.label} value={item.value} />
                ))}
              </div>
            </section>
          ) : null}
        </div>

        <div className='dashboard-v2__withdrawal-form__footer'>
          <p className='dashboard-v2__withdrawal-form__footer-note'>
            {investmentDetailsFooterNote(investment.status, needsWalletPayment)}
          </p>
          <div className='dashboard-v2__withdrawal-form__footer-actions'>
            {canCompletePayment ? (
              <button
                type='button'
                className='primary'
                onClick={() => onCompletePayment(investment)}
                data-testid='ongoing-investment-complete-payment'
              >
                Complete payment
              </button>
            ) : null}
            {canViewPaymentSummary ? (
              <button
                type='button'
                className='secondary'
                onClick={() => onViewPaymentSummary(investment)}
                data-testid='ongoing-investment-view-payment-summary'
              >
                View payment summary
              </button>
            ) : null}
            <button type='button' className='secondary' onClick={resetAndClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
